import { useQueryClient } from "@tanstack/react-query";
import type {
  CatalogPurchaseResponse,
  CatalogResponse,
  HomeLook,
  HomeStateResponse,
  HomeUpgradeResponse,
  InventoryItem,
  MyHomeResponse,
  VisitorAccess,
  WalletsResponse,
} from "@townsy/contracts";
import { errorMessage } from "../../api/client";
import { patchHomeAccess, patchHomeLook, postDecorationPlace, postDecorationRemove, postResetLayout, postSendVisitorsOut } from "../../api/endpoints";
import { queryKeys, useBootstrapQuery, useCatalogQuery, useInventoryQuery, useMyHomeQuery, useWalletsQuery } from "../../api/queries";
import type { TownSession } from "../../app/townSession";
import { runLayoutChange, type LayoutOutcome } from "../../homes/commands";
import { mergeHomeState, type DecorScope, type HomeInputs, type LoadState, type MyHome, type Viewer } from "../../homes/homeLogic";

/**
 * Everything the homes UI reads (bootstrap viewer, catalog, the resident's home, inventory and
 * coins) and every change it can ask the server for, shared by the in-town panels and the 2D
 * `/directory` page. Balances, ownership and inventory are always re-read from the API after a
 * change; nothing is computed locally.
 */
export interface HomeData {
  bootstrapReady: boolean;
  signedIn: boolean;
  viewer: Viewer;
  catalog: CatalogResponse | null;
  catalogState: "loading" | "error" | "ready";
  catalogError: string | null;
  refetchCatalog(): void;
  home: MyHome | null;
  homeState: LoadState;
  inventory: InventoryItem[];
  inventoryState: LoadState;
  wallets: WalletsResponse | null;
  walletState: LoadState;
  /** Inputs for `itemBuyAvailability` / `upgradeAvailability`. */
  inputs: HomeInputs;
}

const stateOf = (enabled: boolean, q: { data?: unknown; error: unknown }): LoadState => (!enabled ? "none" : q.data ? "ready" : q.error ? "error" : "loading");

/** `active` = false keeps a mounted-but-hidden panel from fetching anything. */
export function useHomeData(active = true): HomeData {
  const boot = useBootstrapQuery();
  const signedIn = Boolean(boot.data?.session.userId);
  const resident = signedIn && active;
  const viewer: Viewer = {
    isGuest: !signedIn,
    emailVerified: boot.data?.session.emailVerified ?? false,
    featureEnabled: boot.data?.features.residentialProgressionEnabled ?? true,
  };
  const catalogQ = useCatalogQuery(active);
  const walletsQ = useWalletsQuery(resident);
  const homeQ = useMyHomeQuery(resident);
  const inventoryQ = useInventoryQuery(resident);
  const walletState = stateOf(signedIn, walletsQ);
  const homeState = stateOf(signedIn, homeQ);
  const home = homeQ.data?.home ?? null;
  const wallets = walletsQ.data ?? null;
  return {
    bootstrapReady: Boolean(boot.data),
    signedIn,
    viewer,
    catalog: catalogQ.data ?? null,
    catalogState: catalogQ.data ? "ready" : catalogQ.error ? "error" : "loading",
    catalogError: catalogQ.error ? errorMessage(catalogQ.error) : null,
    refetchCatalog: () => void catalogQ.refetch(),
    home,
    homeState,
    inventory: inventoryQ.data?.items ?? [],
    inventoryState: stateOf(signedIn, inventoryQ),
    wallets,
    walletState,
    inputs: { viewer, homeState, home, wallets, walletState },
  };
}

export interface PlaceInput {
  inventoryInstanceId: string;
  scope: DecorScope;
  anchorId: string;
  rotation: number;
}

/** Server changes to a home, with the cache/scene follow-up each needs. */
export interface HomeActions {
  afterItemPurchase(res: CatalogPurchaseResponse): void;
  afterUpgrade(res: HomeUpgradeResponse): void;
  /** Prices, the home or balances were stale: refetch them before the next attempt. */
  refreshStale(): void;
  /** Re-read the home and inventory (after a 409). */
  refetchHome(): Promise<void>;
  saveLook(home: MyHome, look: HomeLook): Promise<LayoutOutcome>;
  /** ADR-0019: open or close the home to visitors (versioned like every home change). */
  setAccess(home: MyHome, access: VisitorAccess): Promise<LayoutOutcome>;
  /** ADR-0019: ask everyone else inside to leave (not a layout change: nothing to merge). */
  sendVisitorsOut(home: MyHome): Promise<{ ok: true } | { ok: false; message: string }>;
  place(home: MyHome, input: PlaceInput): Promise<LayoutOutcome>;
  remove(home: MyHome, scope: DecorScope, anchorId: string): Promise<LayoutOutcome>;
  resetLayout(home: MyHome): Promise<LayoutOutcome>;
}

export function useHomeActions(session: TownSession | null): HomeActions {
  const qc = useQueryClient();
  const invalidate = (...keys: ReadonlyArray<readonly unknown[]>) => {
    for (const queryKey of keys) void qc.invalidateQueries({ queryKey });
  };
  const refetchHome = async () => {
    await Promise.all([qc.refetchQueries({ queryKey: queryKeys.myHome }), qc.refetchQueries({ queryKey: queryKeys.inventory })]);
  };
  const applyState = (state: HomeStateResponse) => {
    const stages = qc.getQueryData<CatalogResponse>(queryKeys.catalog)?.stages ?? null;
    qc.setQueryData<MyHomeResponse>(queryKeys.myHome, (prev) => (prev?.home ? { home: mergeHomeState(prev.home, state, stages) } : prev));
    invalidate(queryKeys.myHome, queryKeys.inventory, queryKeys.properties, queryKeys.property(state.propertyId));
    session?.afterHomeChanged(state.propertyId, state);
  };
  const run = async (send: () => Promise<HomeStateResponse>): Promise<LayoutOutcome> => {
    const outcome = await runLayoutChange(send, {
      onConflict: async () => {
        await refetchHome();
        // Another tab or device changed the home: the town view re-reads it too.
        const id = qc.getQueryData<MyHomeResponse>(queryKeys.myHome)?.home?.propertyId;
        if (id) session?.afterHomeChanged(id, null);
      },
    });
    if (outcome.ok) applyState(outcome.state);
    return outcome;
  };
  return {
    afterItemPurchase: () => invalidate(queryKeys.wallets, queryKeys.inventory),
    afterUpgrade: (res) => {
      invalidate(queryKeys.wallets, queryKeys.myHome, queryKeys.properties, queryKeys.property(res.propertyId));
      session?.afterHomeChanged(res.propertyId, null);
    },
    refreshStale: () => invalidate(queryKeys.catalog, queryKeys.wallets, queryKeys.myHome, queryKeys.inventory),
    refetchHome,
    saveLook: (home, look) => run(() => patchHomeLook(home.propertyId, { look, expectedLayoutVersion: home.layoutVersion })),
    setAccess: (home, access) => run(() => patchHomeAccess(home.propertyId, { access })),
    sendVisitorsOut: async (home) => {
      try {
        await postSendVisitorsOut(home.propertyId);
        return { ok: true };
      } catch (err) {
        return { ok: false, message: errorMessage(err) };
      }
    },
    place: (home, input) => run(() => postDecorationPlace(home.propertyId, { ...input, expectedLayoutVersion: home.layoutVersion })),
    remove: (home, scope, anchorId) => run(() => postDecorationRemove(home.propertyId, { scope, anchorId, expectedLayoutVersion: home.layoutVersion })),
    resetLayout: (home) => run(() => postResetLayout(home.propertyId, { expectedLayoutVersion: home.layoutVersion })),
  };
}
